<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('notifications.notificationsLabel') }}</span>
        <h3 class="page-title">{{ $t('notifications.editNotification') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/powiadomienia')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>

        <d-button theme="success" @click.prevent="validateForm">
          <i class="material-icons">edit</i>
          {{ $t('ui.saveChanges') }}
        </d-button>
      </d-col>
    </d-row>

    <d-alert
      theme="warning"
      class="d-flex"
      :show="isConnectedWithContact">
      <span
        v-if="isConnectedWithContact"
        class="pt-1">
        {{ $t('notifications.isConnectedWithContact') }}
      </span>
      <d-button
        size="sm"
        theme="light"
        class="ml-auto"
        @click.prevent="navigateToContact()">
        {{ $t('notifications.viewConnectedContact') }}
      </d-button>
    </d-alert>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs
          v-if="loadedData && !loadError"
          v-sticky-tabs
          card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="notification-edit-form" tag="form">
              <div class="form-group">
                <label for="name">
                  {{ $t('notifications.form.description') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="description"
                  v-slot="{ errors }">
                  <d-textarea
                    v-model="description"
                    :value.prop="description"
                    :state="errors.length ? 'invalid' : null"
                    :rows="5" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <d-row>
                <d-col cols="2">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('notifications.form.date') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="date"
                      v-slot="{ errors }">
                      <d-datepicker
                        v-model="date"
                        :monday-first="true"
                        format="yyyy-MM-dd"
                        :placeholder="$t('ui.chooseDate')"
                        :disabled-dates="pastDates"
                        :language="translationsForDatePicker" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('notifications.form.time') }}:
                    </label>

                    <div class="d-flex align-middle">
                      <d-form-select
                        v-model="timeHours"
                        class="mr-2">
                        <option
                          v-for="(hour, index) of hoursList"
                          :key="'time-hour-' + index"
                          :value="hour">
                          {{ hour }}
                        </option>
                      </d-form-select>

                      <span style="line-height: 30px;">:</span>

                      <d-form-select
                        v-model="timeMinutes"
                        class="ml-2">
                        <option
                          v-for="(minute, index) of minutesList"
                          :key="'time-minute-' + index"
                          :value="minute">
                          {{ minute }}
                        </option>
                      </d-form-select>
                    </div>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <label>
                      {{ $t('notifications.form.isRepeatable') }}:
                    </label>

                    <div>
                      <d-checkbox
                        inline
                        v-model="is_repeatable"
                        toggle />
                    </div>
                  </div>
                </d-col>

                <d-col
                  v-if="is_repeatable"
                  cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('notifications.form.frequency') }}:
                    </label>

                    <div>
                      <validation-provider
                        rules="required"
                        name="frequency"
                        v-slot="{ errors }">
                        <d-form-select
                          v-model="frequency"
                          :state="errors.length ? 'invalid' : null"
                          :options="[{
                            text: $t('notifications.form.frequencyValues.selectValue'),
                            value: null
                          },{
                            text: $t('notifications.form.frequencyValues.daily'),
                            value: 'DAILY'
                          }, {
                            text: $t('notifications.form.frequencyValues.weekly'),
                            value: 'WEEKLY'
                          }, {
                            text: $t('notifications.form.frequencyValues.monthly'),
                            value: 'MONTHLY'
                          }]" />
                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                    </div>
                  </div>
                </d-col>

                <d-col
                  v-if="is_repeatable"
                  cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('notifications.form.repeatNumber') }}:
                    </label>

                    <div>
                      <validation-provider
                        rules="required|only-integer-numbers"
                        name="repeat-number"
                        v-slot="{ errors }">
                        <d-input
                          type="number"
                          min="0"
                          step="1"
                          :state="errors.length ? 'invalid' : null"
                          v-model="repeat_number" />
                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>

                      <small class="note text-light">
                        {{ $t('notifications.form.repeatNumberNote') }}
                      </small>
                    </div>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('notifications.form.showInDashboard') }}:
                    </label>

                    <div>
                      <d-checkbox
                        inline
                        v-model="show_in_dashboard"
                        :disabled="isConnectedWithContact"
                        toggle />
                    </div>
                  </div>
                </d-col>

                <d-col
                  v-if="show_in_dashboard || send_reminder"
                  cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('notifications.form.reminderBefore') }}
                    </label>

                    <validation-provider
                      rules="required|only-integer-numbers"
                      name="reminder-before"
                      v-slot="{ errors }">
                      <d-input-group>
                        <d-input
                          type="number"
                          min="1"
                          max="100"
                          step="1"
                          class="text-right"
                          :state="errors.length ? 'invalid' : null"
                          :disabled="isConnectedWithContact"
                          v-model="reminder_before" />
                        <d-input-group-text slot="append">
                          h
                        </d-input-group-text>
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('notifications.form.sendReminder') }}:
                    </label>

                    <div>
                      <d-checkbox
                        inline
                        v-model="send_reminder"
                        :disabled="isConnectedWithContact"
                        toggle />
                    </div>
                  </div>
                </d-col>

                <d-col
                  v-if="send_reminder"
                  cols="9">
                  <div
                    v-if="send_reminder && !isConnectedWithContact"
                    class="form-group mt-3"
                    style="width: 504px;">
                    <label class="d-flex">
                      <span class="pt-1">{{ $t('notifications.form.emailCarbonCopy') }}:</span>

                      <d-button
                        theme="success"
                        class="ml-auto"
                        @click.prevent="email_carbon_copy.push('');">
                        <i class="material-icons">add</i>
                        {{ $t('notifications.form.addCC') }}
                      </d-button>
                    </label>

                    <div
                      v-for="(email, index) of email_carbon_copy"
                      :key="'email-cc-' + index"
                      class="d-flex mb-1">
                      <validation-provider
                        rules="required|email"
                        name="email-carbon-copy"
                        v-slot="{ errors }">
                        <d-input
                          v-model="email_carbon_copy[index]"
                          :state="errors.length ? 'invalid' : null"
                          style="width: 450px" />
                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                      <d-button
                        theme="danger"
                        @click.prevent="removeEmailCarbonCopy(index)"
                        class="ml-2">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </div>
                  </div>
                </d-col>
              </d-row>
            </validation-observer>
          </d-tab>
        </d-tabs>

        <div v-if="!loadedData" class="pb-3 pt-3 text-center  overlay-loading">
          {{ $t('ui.dataLoading') }}&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
        </d-alert>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import datepickerTranslations from './../../data/datepicker-translations.js';
import FormUtils from '../../utils/FormUtils.js';
import getPrefferedLanguage from './../../utils/i18nUtils.js';

export default {
  name: 'tag-edit',
  computed: {
    hoursList () {
      return Array.from({ length: 24 }, (v, k) => k < 10 ? '0' + k : (k).toString());
    },
    minutesList () {
      return Array.from({ length: 60 }, (v, k) => k < 10 ? '0' + k : (k).toString());
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    pastDates () {
      let yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      return {
        to: yesterday
      };
    },
    isConnectedWithContact () {
      return !!this.contact_id;
    },
    isConnectedWithMarketContact () {
      return !!this.market_contact_id;
    },
    isConnectedWithPotentialContact () {
      return !!this.potential_contact_id;
    }
  },
  data () {
    return {
      description: '',
      date: new Date(),
      frequency: null,
      is_repeatable: false,
      repeat_number: 0,
      time: null,
      timeHours: '00',
      timeMinutes: '00',
      contact_id: null,
      market_contact_id: null,
      potential_contact_id: null,
      show_in_dashboard: false,
      send_reminder: false,
      reminder_before: 1,
      email_carbon_copy: [],
      loadedData: false,
      loadError: false
    };
  },
  mounted () {
    FormUtils.loadItemData(this, {
      endpointBase: '/notifications/item/',
      instanceFields: ['description', 'contact_id', 'market_contact_id', 'potential_contact_id', 'frequency', 'repeat_number'],
      loadedKey: 'loadedData',
      errorKey: 'loadError',
      successAction: (response) => {
        this.date = new Date(response.date);
        this.timeHours = response.time.split(':')[0];

        if (parseInt(this.timeHours, 10) < 10) {
          this.timeHours = '0' + parseInt(this.timeHours, 10);
        }

        this.timeMinutes = response.time.split(':')[1];

        if (parseInt(this.timeMinutes, 10) < 10) {
          this.timeMinutes = '0' + parseInt(this.timeMinutes, 10);
        }

        if (response.email_carbon_copy) {
          this.email_carbon_copy = response.email_carbon_copy.split(',');
        } else {
          this.email_carbon_copy = [];
        }

        if (response.reminder_before) {
          this.reminder_before = parseInt(response.reminder_before, 10);
        }

        this.show_in_dashboard = !!response.show_in_dashboard;
        this.send_reminder = !!response.send_reminder;
        this.is_repeatable = !!response.is_repeatable;
      }
    });
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['notification-edit-form'], this.submitForm);
    },
    submitForm () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/notifications/update/' + this.$route.params.id,
        formData: this.prepareFormData(),
        successTitle: this.$t('notifications.form.editNotificationSuccessTitle'),
        successText: this.$t('notifications.form.editNotificationSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('notifications.form.editNotificationErrorText'),
        successAction: () => {
          this.$router.push('/powiadomienia')
        }
      });
    },
    prepareFormData () {
      let formData = {
        description: this.description,
        date: [this.date.getFullYear(), this.date.getMonth() + 1, this.date.getDate()].map(n => n < 10 ? '0' + n : n).join('-'),
        time: [this.timeHours, this.timeMinutes].join(':'),
        show_in_dashboard: +this.show_in_dashboard,
        send_reminder: +this.send_reminder,
        is_repeatable: +this.is_repeatable
      };

      if (this.show_in_dashboard || this.send_reminder) {
        formData.reminder_before = this.reminder_before;
      }

      if (this.send_reminder) {
        formData.email_carbon_copy = this.email_carbon_copy.join(',');
      }

      if (this.is_repeatable) {
        formData.frequency = this.frequency;
        formData.repeat_number = this.repeat_number;
      }

      return formData;
    },
    removeEmailCarbonCopy (index) {
      this.email_carbon_copy.splice(index, 1);
    },
    navigateToContact () {
      if (this.isConnectedWithContact) {
        this.$router.push('/kontakty-z-klientami/edycja/' + this.contact_id);
      }
    }
  }
};
</script>
